// src/hooks/useContractDocuments.ts
// Hook for fetching and managing contract documents with extracted text

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface ContractDocument {
  id: string;
  contract_id: string;
  document_name: string;
  document_url: string;
  document_type: string;
  file_type: string;
  file_size: number;
  raw_text: string | null;
  text_hash: string | null;
  page_count: number | null;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  error_message: string | null;
  processed_at: string | null;
  created_at: string;
  updated_at: string;
}

interface UseContractDocumentsOptions {
  noticeId: string;
  includeExtractedText?: boolean;
  onlyCompleted?: boolean;
}

export function useContractDocuments({
  noticeId,
  includeExtractedText = true,
  onlyCompleted = false
}: UseContractDocumentsOptions) {
  const [documents, setDocuments] = useState<ContractDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchDocuments = useCallback(async () => {
    if (!noticeId) {
      setDocuments([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Build select query based on options
      const columns = includeExtractedText
        ? '*'
        : 'id, contract_id, document_name, document_url, document_type, file_type, file_size, status, error_message, page_count, processed_at, created_at, updated_at';

      let query = supabase
        .from('contract_documents')
        .select(columns)
        .eq('contract_id', noticeId)
        .order('created_at', { ascending: false });

      if (onlyCompleted) {
        query = query.eq('status', 'completed');
      }

      const { data, error: queryError } = await query;

      if (queryError) {
        throw queryError;
      }

      setDocuments(data || []);
    } catch (err) {
      console.error('Error fetching contract documents:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch documents'));
    } finally {
      setLoading(false);
    }
  }, [noticeId, includeExtractedText, onlyCompleted]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Get combined extracted text from all documents
  const getCombinedText = useCallback((): string => {
    const completedDocs = documents.filter(
      doc => doc.status === 'completed' && doc.raw_text
    );

    if (completedDocs.length === 0) {
      return '';
    }

    return completedDocs
      .map(doc => {
        const header = `=== ${doc.document_name} ===\n`;
        return header + (doc.raw_text || '');
      })
      .join('\n\n');
  }, [documents]);

  // Get documents summary for proposal context
  const getDocumentsSummary = useCallback((): {
    totalDocuments: number;
    extractedDocuments: number;
    totalCharacters: number;
    documentNames: string[];
  } => {
    const completedDocs = documents.filter(
      doc => doc.status === 'completed' && doc.raw_text
    );

    return {
      totalDocuments: documents.length,
      extractedDocuments: completedDocs.length,
      totalCharacters: completedDocs.reduce(
        (sum, doc) => sum + (doc.raw_text?.length || 0),
        0
      ),
      documentNames: documents.map(doc => doc.document_name)
    };
  }, [documents]);

  // Check if documents are ready for proposal generation
  const isReadyForProposal = useCallback((): boolean => {
    const hasDocuments = documents.length > 0;
    const hasExtractedText = documents.some(
      doc => doc.status === 'completed' && doc.raw_text
    );
    return hasDocuments && hasExtractedText;
  }, [documents]);

  return {
    documents,
    loading,
    error,
    refresh: fetchDocuments,
    getCombinedText,
    getDocumentsSummary,
    isReadyForProposal
  };
}

// Helper function to get document content for a contract (can be used outside React components)
export async function getContractDocumentText(noticeId: string): Promise<{
  text: string;
  documentCount: number;
  charCount: number;
}> {
  try {
    const { data, error } = await supabase
      .from('contract_documents')
      .select('document_name, raw_text')
      .eq('contract_id', noticeId)
      .eq('status', 'completed')
      .not('raw_text', 'is', null);

    if (error) {
      console.error('Error fetching document text:', error);
      return { text: '', documentCount: 0, charCount: 0 };
    }

    if (!data || data.length === 0) {
      return { text: '', documentCount: 0, charCount: 0 };
    }

    const combinedText = data
      .map(doc => `=== ${doc.document_name} ===\n${doc.raw_text}`)
      .join('\n\n');

    return {
      text: combinedText,
      documentCount: data.length,
      charCount: combinedText.length
    };
  } catch (error) {
    console.error('Error in getContractDocumentText:', error);
    return { text: '', documentCount: 0, charCount: 0 };
  }
}
